// Lightweight fetch wrapper. Same-origin in production, vite-proxied in dev.
// Cookie credentials always included so the opaque session cookie travels.
export class ApiError extends Error {
    status;
    detail;
    constructor(status, detail) {
        super(`HTTP ${status}: ${detail}`);
        this.status = status;
        this.detail = detail;
    }
}
async function request(method, path, body, signal) {
    const headers = { Accept: "application/json" };
    if (body !== undefined)
        headers["Content-Type"] = "application/json";
    const r = await fetch(path, {
        method,
        headers,
        credentials: "include",
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
    });
    if (!r.ok) {
        let detail = r.statusText;
        try {
            const j = await r.json();
            detail = j.detail || detail;
        }
        catch {
            /* keep statusText */
        }
        throw new ApiError(r.status, detail);
    }
    if (r.status === 204)
        return undefined;
    return (await r.json());
}
export const api = {
    get: (p, signal) => request("GET", p, undefined, signal),
    post: (p, body, signal) => request("POST", p, body, signal),
    patch: (p, body) => request("PATCH", p, body),
    put: (p, body) => request("PUT", p, body),
    del: (p) => request("DELETE", p),
};
// A stable, collision-free synthetic bID for a central event. The SPA keys rows
// and selection on (customer, bID); central events have no shard bID, so derive
// a negative number from the event id's first 8 hex digits. Negative keeps it
// out of the shard bID space.
export function centralSyntheticBid(eventId) {
    const hex = eventId.replace(/-/g, "").slice(0, 8);
    const n = parseInt(hex, 16);
    return Number.isFinite(n) ? -(n % 2147483647) - 1 : -1;
}
export function centralToEventRecord(r) {
    return {
        bID: centralSyntheticBid(r.event_id),
        customer: r.customer_id,
        sSourcePath: "",
        sSource: `${r.nvr_id}-central`,
        bCamera: r.camera,
        sCameraName: r.camera_name ?? null,
        tEventStart: r.event_time,
        sThumbnailPath: null,
        sModel: "",
        sDescription: r.description,
        bLatencyMs: 0,
        fCost: "0",
        sUseful: r.useful ?? null,
        central: { event_id: r.event_id, media_ref: r.media_ref, score: r.score, media_available: r.media_available },
    };
}
export const clipSrc = (ev) => ev.central ? `/api/media/${encodeURIComponent(ev.central.media_ref)}` : `/clip-${ev.customer}${ev.sSourcePath}`;
// Preview thumbnail URL for an event, or null when none is available. Central
// events serve a stored JPEG from the cloud (/api/media/<ref>/thumb); shard
// events use the per-customer NVR thumbnail alias. Used for the grid image and
// the player poster. Consumers must handle a 404 (fall back to a placeholder /
// play affordance) since a thumbnail is best-effort.
export const thumbSrc = (ev) => ev.central
    ? `/api/media/${encodeURIComponent(ev.central.media_ref)}/thumb`
    : ev.sThumbnailPath
        ? `/thumb-${ev.customer}${ev.sThumbnailPath}`
        : null;
export const apiMe = () => api.get("/api/me");
export const apiLogout = () => api.post("/api/auth/logout", {});
export const apiEvents = (customer, since, until, limit = 100, includeEmpty = false, signal, bCameras) => {
    const q = new URLSearchParams();
    if (customer)
        q.set("customer", customer);
    if (since)
        q.set("since", since);
    if (until)
        q.set("until", until);
    if (includeEmpty)
        q.set("include_empty", "true");
    q.set("limit", String(limit));
    // Repeated `bCamera=N` params, one per selected camera — matches the
    // shard's URL parser (which also accepts comma-joined forms but the
    // repeated form is simpler for URLSearchParams).
    if (bCameras && bCameras.length > 0) {
        for (const c of bCameras)
            q.append("bCamera", String(c));
    }
    return api.get(`/api/events?${q.toString()}`, signal);
};
export const apiSearch = (q, customer, since, until, limit = 50, signal, bCameras) => {
    const body = { q, customer, since, until, limit };
    if (bCameras && bCameras.length > 0)
        body.bCameras = bCameras;
    return api.post("/api/search", body, signal);
};
export const apiSetRating = (customer, bID, rating) => api.patch(`/api/events/${customer}/${bID}/rating`, { rating });
export const apiSetCentralRating = (eventId, rating) => api.patch(`/api/central/events/${encodeURIComponent(eventId)}/rating`, { rating });
// Rate whichever backend owns the event.
export const apiRateEvent = (ev, rating) => ev.central ? apiSetCentralRating(ev.central.event_id, rating) : apiSetRating(ev.customer, ev.bID, rating);
export const apiEventByBid = (customer, bID, signal) => api.get(`/api/events/${encodeURIComponent(customer)}/${bID}`, signal);
// Durable deep-link hydrate: `key` is base64url(sSourcePath) from the alert
// email. Resolves by clip path, so it survives GC delete + re-ingest that
// reassigns bIDs (unlike apiEventByBid). Key chars are base64url-safe; encode
// anyway as defense against a malformed key reaching the path.
export const apiEventByPath = (customer, key, signal) => api.get(`/api/events/${encodeURIComponent(customer)}/by-path/${encodeURIComponent(key)}`, signal);
export const apiCostSummary = (customer, since) => {
    const q = new URLSearchParams();
    if (customer)
        q.set("customer", customer);
    if (since)
        q.set("since", since);
    return api.get(`/api/cost-summary?${q.toString()}`);
};
export const apiLoginUrl = (redirect) => {
    const q = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";
    return `/api/auth/login${q}`;
};
